/* EntitySelect — the antd entity dropdown: server-side search, page-on-scroll.
 *
 * docs/design/spec/02-components.md maps the mockups' `.combo` to
 * `<Select showSearch onPopupScroll>`; this is that component, sharing `modelMap`,
 * `EntityDropDownSettings` and `maptoSelect2Objects` with the legacy react-select
 * `EntityDropdown` so option shapes stay identical across both.
 *
 * Why not reuse `EntityDropdown` directly: it renders react-select, which cannot take the
 * `ConfigProvider` theme — a filter row would mix two control kits, two focus rings and two
 * radii on one line. Migrate a screen's dropdowns to this component when that screen is
 * redesigned; do not swap it in under a legacy page.
 *
 * Value contract, deliberately the same as EntityDropdown's so `filterUrl()` keeps working:
 *   isMulti  → an array of full option objects  (`{...entity, value, label}`)
 *   single   → whatever the caller stores; an id or a full object are both accepted in,
 *              and `onChange` always hands back the full option object (or null).
 * A bare id cannot render its own label, so pass `seed` when one is preselected (e.g. the
 * supervisor's own county, which rides on `me` as a full object) — otherwise the first page of
 * options is fetched on mount to resolve it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { useSelector } from "react-redux";

import { DROPDOWN_PAGE_SIZE, EntityDropDownSettings, FILTER_DEBOUNCE_MS, modelMap } from "../config/config";
import { ENTITY_DROPDOWN_SETTINGS } from "../config/lang";
import { call } from "../util/action";
import { maptoSelect2Objects, translate } from "../util/util";
import "./entity-select.scss";

/** How close to the bottom of the option list counts as "load the next page". */
const SCROLL_SLACK = 48;

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

const labelOf = o => {
  if (!o || typeof o !== "object") return String(o ?? "");
  return o.label || o.name || [o.first_name, o.last_name].filter(Boolean).join(" ") || String(o.id ?? "");
};

/** antd wants `{value,label}`; we keep the full entity object beside it for `filterUrl`. */
const toAntd = o => ({ value: idOf(o), label: labelOf(o) });

export default function EntitySelect({
  entity,
  value,
  onChange,
  isMulti = false,
  seed = null,
  county,
  moduleFilter,
  extraQuery,
  prefix,
  placeholder,
  disabled,
  allowClear = true,
  className = "",
  id,
  /* Chip overflow. A tight filter row keeps the design's one-chip-plus-`+N` (the default 1); a
     full-width form field passes `"responsive"` so it shows as many chips as fit rather than
     hiding all but one behind `+N` in a field with room to spare. */
  maxTagCount = 1,
  /* A tall, wrapping, scrollable chip area (a group's point list) instead of the default one-row
     control. Overrides the nowrap chrome in `entity-select.scss`; pair with `maxTagCount`
     undefined so every chip shows rather than collapsing into `+N`. */
  tall = false,
  /* For a select with no visible <label> of its own — every field needs an accessible name. */
  ariaLabel,
  /**
   * Called with the full option objects once bare ids have been resolved to labels.
   *
   * A shared or bookmarked URL carries ids only, so a caller that restores its state from one
   * holds `{ id: 52 }` and can only render "52". This component already fetches page 1 to name
   * such a value for its own display; the callback hands that work back rather than making every
   * caller repeat the request.
   */
  onResolve,
}) {
  const lang = useSelector(state => state.me?.language) || "en";
  const t = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  /* How many rows exist server-side, for the panel's `loaded / total` counter — the design's
     `.combo__foot`, which is what tells the user the list is paged rather than short. */
  const [total, setTotal] = useState(0);
  const page = useRef(1);
  const term = useRef("");
  /* Only the newest request may paint: a fast typist otherwise gets an older page's
     options landing on top of the current query's. */
  const seq = useRef(0);
  const debounce = useRef(null);
  const seeded = useRef(false);

  const scope = `${entity}|${county ?? ""}|${moduleFilter ?? ""}|${JSON.stringify(extraQuery || null)}`;

  const fetchPage = useCallback(
    (nextPage, query, append) => {
      const base = String(modelMap[entity] || entity).replace(/\/+$/, "");
      let url = `${base}?page=${nextPage}&page_size=${DROPDOWN_PAGE_SIZE}`;
      if (query) url += `&search=${encodeURIComponent(query)}`;
      // Points are scoped by the region and network filters that sit beside them.
      if (entity === "point" || entity === "points") url += `&locality__county=${county || ""}&network=${moduleFilter || ""}`;
      Object.entries(extraQuery || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") url += `&${k}=${encodeURIComponent(v)}`;
      });

      const mine = ++seq.current;
      setLoading(true);
      call({ method: "GET", url }, response => {
        if (mine !== seq.current) return;
        const rows = Array.isArray(response) ? response : response?.results || [];
        const mapped = maptoSelect2Objects(rows.slice(), entity) || [];
        page.current = nextPage;
        setHasMore(Array.isArray(response) ? false : Boolean(response?.next));
        setTotal(Array.isArray(response) ? response.length : response?.count ?? 0);
        setOptions(prev => {
          const byValue = new Map((append ? prev : []).map(o => [o.value, o]));
          mapped.forEach(o => byValue.set(o.value, o));
          return Array.from(byValue.values());
        });
        setLoading(false);
      });
    },
    [entity, county, moduleFilter, extraQuery]
  );

  /* A scope change (region → points, network → points) invalidates the loaded list; keeping it
     would serve the previous scope's options until the user scrolled. */
  useEffect(() => {
    setOptions([]);
    setHasMore(true);
    setTotal(0);
    page.current = 1;
    term.current = "";
    seeded.current = false;
  }, [scope]);

  /* Resolve a preselected value to a label. A full object is self-describing; `seed` covers a
     bare id without a request (the supervisor's own county); anything still unresolved falls
     back to showing the raw id and triggers one page-1 fetch to try to name it. That last case
     is how a shared/bookmarked URL arrives — it carries ids only, so a value outside the first
     page of options (points, products) stays as its id. Filtering is still correct. */
  const selected = useMemo(() => {
    const list = isMulti ? (Array.isArray(value) ? value : []) : value === "" || value === null || value === undefined ? [] : [value];
    return list.map(v => {
      if (v && typeof v === "object" && (v.label || v.name || v.first_name || v.last_name)) return v;
      const key = idOf(v);
      if (seed && String(idOf(seed)) === String(key)) return seed;
      return options.find(o => String(o.value) === String(key)) || { id: key, label: String(key), unresolved: true };
    });
  }, [value, isMulti, seed, options]);

  const hasUnresolved = selected.some(o => o.unresolved);
  useEffect(() => {
    if (hasUnresolved && !seeded.current && !loading) {
      seeded.current = true;
      fetchPage(1, "", false);
    }
  }, [hasUnresolved, loading, fetchPage]);

  /* Report the names back up. The signature guard is what keeps this from looping: a caller that
     stores the label re-renders this component, which recomputes `selected` to the same values —
     without it, "report → parent state → re-render → report" would never settle. */
  const reported = useRef("");
  useEffect(() => {
    if (!onResolve) return;
    const named = selected.filter(o => o && !o.unresolved && labelOf(o));
    if (!named.length) return;
    const signature = named.map(o => `${idOf(o)}